/**
 * The monogram avatar — a person, in one character.
 *
 * Nob has no avatar to show. It never asks for a photo, it does not proxy X's,
 * and a room full of identical grey circles tells a reader nothing. So the
 * colour does the work: it is derived from the ADDRESS, which is the one thing
 * about a door that cannot change under it, so the same door is the same colour
 * in the sidebar, in a search result and in the list of conversations — and two
 * different doors are reliably two different colours to scan past.
 *
 * The palette is small, muted and chosen not to compete with the accent, which
 * is the door's own colour and means "act on this" everywhere else in the
 * product. Each hue carries its own foreground so the character is legible on
 * it rather than nearly legible.
 *
 * These seven are the one place in the app that is NOT a theme role, and they
 * stay that way deliberately: a hue here identifies a PERSON, and somebody's
 * avatar changing colour when the reader picks a different skin would make the
 * one thing on the screen that is supposed to be recognisable unrecognisable.
 * Pale fills with ink type read the same on every ground, so they can afford to
 * be fixed — which is exactly why the eighth entry, the fallback for a name
 * with no hue, IS a role: it is furniture, not an identity.
 *
 * Class names are written out in full: Tailwind reads this file as text, and a
 * composed `bg-${hue}-300` would simply not be generated.
 */

type Hue = { bg: string; fg: string };

const PALETTE: Hue[] = [
  { bg: "bg-sky-300/85", fg: "text-ink" },
  { bg: "bg-emerald-300/85", fg: "text-ink" },
  { bg: "bg-rose-300/85", fg: "text-ink" },
  { bg: "bg-violet-300/85", fg: "text-ink" },
  { bg: "bg-teal-300/85", fg: "text-ink" },
  { bg: "bg-orange-300/85", fg: "text-ink" },
  { bg: "bg-indigo-400/85", fg: "text-ink" },
  { bg: "bg-raised-strong", fg: "text-text" },
];

/** FNV-1a, 32-bit. Stable across every browser and every render — a hash, not a random. */
function hash(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function monogramHue(key: string): Hue {
  return PALETTE[hash(key.toLowerCase()) % PALETTE.length] as Hue;
}

/** The first letter or digit in a name — never a bracket, an emoji half, or a space. */
function initialOf(name: string, handle: string): string {
  const source = `${name} ${handle}`;
  const match = /\p{L}|\p{N}/u.exec(source);
  return (match?.[0] ?? "?").toUpperCase();
}

const SIZES = {
  sm: "h-9 w-9 text-[13px]",
  md: "h-10 w-10 text-sm",
  /** The profile room, where the monogram is the portrait rather than a marker. */
  lg: "h-16 w-16 text-2xl",
} as const;

/**
 * `handle` keys the colour; `name` supplies the character, falling back to the
 * handle when a door has no display name of its own. Decorative by default —
 * the name is always beside it in the same row, and a screen reader that says
 * "M, Maya Chen" is reading the same thing twice.
 */
export function Monogram({
  name,
  handle,
  size = "sm",
  className = "",
}: {
  name: string;
  handle: string;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const hue = monogramHue(handle || name);
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-medium ${
        SIZES[size]
      } ${hue.bg} ${hue.fg} ${className}`}
    >
      {initialOf(name, handle)}
    </span>
  );
}
